import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import { Provider } from "../../models/Provider";
import { useState } from "react";
import JobsRepository from "../../repositories/JobsRepository";
import { useQueryClient } from "@tanstack/react-query";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import ConfirmMigrationEntitiesExportToCsvModal, {CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS} from "../common/ConfirmMigrationEntitiesExportToCsvModal";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Checkbox} from "@/components/ui/checkbox";
import {Trash2} from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface CreateExportProvidersToCsvTableProps {
  organizationId: string;
  newProvidersForExport: Provider[];
  setNewProvidersForExport: React.Dispatch<React.SetStateAction<Provider[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CreateExportProvidersToCsvTable: React.FC<CreateExportProvidersToCsvTableProps> = ({
  organizationId,
  newProvidersForExport,
  setNewProvidersForExport,
  userRole,
  isAssignedUser
}) => {
  const requiresReason = useRequiresReason();
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [exportFilesData, setExportFilesData] = useState<boolean>(false);
  const [exportReason, setExportReason] = useState<string | undefined>("");
  const allRows = [...newProvidersForExport];
  const newIds = new Set(newProvidersForExport.map((c) => c.id));
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  return (
    <div className="flex flex-col gap-4 pt-4 max-h-[40%] min-h-[40%]">
      <div className="flex flex-col gap-2">
        {requiresReason ? (
          <ActionConfirmationModal
            actionConfig={ACTION_CONFIGS.EXPORT_PROVIDER}
            onConfirm={(reason) => {
              setConfirmDeletionModalOpen(true)
              setExportReason(reason);
            }}
          >
            <Button disabled={!(newProvidersForExport.length > 0)}>
              Export
            </Button>
          </ActionConfirmationModal>
        ) : (
          <Button
            disabled={!(newProvidersForExport.length > 0)}
            onClick={() => setConfirmDeletionModalOpen(true)}
          >
            Export
          </Button>
        )}
        <div className="flex items-center justify-between">
          <Label className="text-sm">Export files data</Label>
          <Checkbox
            checked={exportFilesData}
            onCheckedChange={(checked) => setExportFilesData(checked as boolean)}
          />
        </div>
      </div>
      <div className={drawerTableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%] min-w-[48px] text-center" />

              <TableHead className="w-[40%]">
                Provider Number
              </TableHead>

              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {allRows.map((deleteProviderJob) => {
              return (
                <TableRow
                  key={deleteProviderJob.id}
                  className={newIds.has(deleteProviderJob.id) ? "bg-primary/10" : ""}
                >
                  <TableCell className="w-[120px] text-center" />
                  <TableCell>
                    <span className="text-sm">
                      {deleteProviderJob.providerNameId}
                    </span>
                  </TableCell>
                  <TableCell className="flex items-center justify-end px-1.5 py-3">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive"
                      aria-label="Cancel"
                      onClick={() => {
                        setNewProvidersForExport((prevRows) =>
                          prevRows.filter(
                            (entity) => entity.id !== deleteProviderJob.id
                          )
                        );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesExportToCsvModal
          open={confirmDeletionModalOpen}
          entityKeys={newProvidersForExport.map((c) => c.providerNameId)}
          exportFilesData={exportFilesData}
          config={CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS.PROVIDERS}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={async () => {
            await jobsRepository.startCsvGenerationFromProviders(
              organizationId,
              newProvidersForExport.map((p) => ({id: p.id, providerNameId: p.providerNameId,})),
              exportFilesData,
              exportReason
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_PROVIDERS, organizationId] });
            setNewProvidersForExport([]);
            setExportReason(undefined);
            setConfirmDeletionModalOpen(false);
            toast.success("Export started");
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default CreateExportProvidersToCsvTable;
